import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "./Navbar";
import { Button } from "./ui/button";
import { LogOut, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";

const AdminLayout = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkAdmin = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        navigate("/customer/login");
        return;
      }

      const { data: profile } = await supabase.from("profiles").select("is_admin").eq("id", session.user.id).single();

      if (!profile?.is_admin) {
        navigate("/");
      }
    };

    checkAdmin();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        navigate("/");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleLogout = async () => {
    try {
      // Clear cart items for this user before signing out
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (userId) {
        const { error: cartError } = await supabase.from("cart_items").delete().eq("user_id", userId);
        if (cartError) {
          console.warn("Failed to clear cart on logout (admin):", cartError);
        }
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast({
        title: "Success",
        description: "You have been logged out successfully",
      });

      navigate("/customer/login");
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col gap-4 mb-8">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Store
            </Link>
          </Button>
          <Button variant="destructive" size="sm" onClick={handleLogout} className="flex items-center gap-2 w-fit">
            <LogOut className="h-4 w-4" />
            Logout of K9Chews Staff Account
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
